import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Tag, Database, Users, Search, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { PROSPECTOR_ENGINE_URL } from '../../lib/engineUrls';

const operations = [
  {
    id: 'create_notion_page',
    name: 'Create Notion Page',
    description: 'Create a new page in a Notion database with structured content',
    icon: Database,
    color: 'green',
    input: 'Content object, Notion database ID, page properties',
    output: 'Notion page URL',
    example: 'Content + DB ID → New Notion page'
  },
  {
    id: 'search_append',
    name: 'Search & Append',
    description: 'Search for existing Notion page and append content to it',
    icon: Search,
    color: 'blue',
    input: 'Search query, content to append, database ID',
    output: 'Updated page URL',
    example: 'Search + Content → Updated page'
  },
  {
    id: 'auto_tag',
    name: 'Auto Tagging',
    description: 'Apply tags based on content classification',
    icon: Tag,
    color: 'purple',
    input: 'Text content',
    output: 'Tags array',
    example: 'AI article → [artificial-intelligence, technology]'
  },
  {
    id: 'extract_entities',
    name: 'Extract Entities',
    description: 'Identify named entities and relationships',
    icon: Users,
    color: 'orange',
    input: 'Text content',
    output: 'Entities object',
    example: 'John works at OpenAI → {person: John, org: OpenAI}'
  }
];

const getColorClasses = (color: string) => {
  const colors = {
    blue: {
      bg: 'bg-[var(--slate-dim)]',
      border: 'border-[var(--slate)]',
      text: 'text-[var(--text-sec)]',
      badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
      hover: 'hover:bg-[var(--slate-dim)]'
    },
    green: {
      bg: 'bg-[var(--green-dim)]',
      border: 'border-[var(--green)]',
      text: 'text-[var(--green)] dark:text-[var(--text-ter)]',
      badge: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
      hover: 'hover:bg-[var(--green-dim)]'
    },
    purple: {
      bg: 'bg-[var(--slate-dim)]',
      border: 'border-[var(--slate)]',
      text: 'text-[var(--text-sec)]',
      badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
      hover: 'hover:bg-[var(--slate-dim)]'
    },
    orange: {
      bg: 'bg-[var(--mustard-dim)]',
      border: 'border-[var(--mustard)]',
      text: 'text-[var(--mustard)]',
      badge: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
      hover: 'hover:bg-[var(--mustard-dim)]'
    }
  };
  return colors[color as keyof typeof colors] || colors.blue;
};

export default function Tagging() {
  const [selectedOperation, setSelectedOperation] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState<any[]>([]);
  
  // Input state for each operation
  const [notionContent, setNotionContent] = useState('');
  const [notionDatabaseId, setNotionDatabaseId] = useState('');
  const [notionApiKey, setNotionApiKey] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [appendContent, setAppendContent] = useState('');
  const [tagContent, setTagContent] = useState('');
  const [entityContent, setEntityContent] = useState('');

  const handleOperation = async (operationId: string) => {
    setSelectedOperation(operationId);
    setIsProcessing(true);
    
    try {
      let result;
      
      switch(operationId) {
        case 'create_notion_page': {
          if (!notionContent || !notionDatabaseId || !notionApiKey) {
            alert('Please provide content, database ID, and API key');
            setIsProcessing(false);
            return;
          }
          // Call Prospector Engine's Notion endpoint
          const createResponse = await fetch(`${PROSPECTOR_ENGINE_URL}/api/notion/create-page`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              apiKey: notionApiKey,
              databaseId: notionDatabaseId,
              content: notionContent
            })
          });
          result = await createResponse.json();
          break;
        }

        case 'search_append': {
          if (!searchQuery || !appendContent || !notionDatabaseId || !notionApiKey) {
            alert('Please provide search query, content to append, database ID, and API key');
            setIsProcessing(false);
            return;
          }
          // Search first, then append
          const searchResponse = await fetch(`${PROSPECTOR_ENGINE_URL}/api/notion/search-pages`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              apiKey: notionApiKey,
              databaseId: notionDatabaseId,
              query: searchQuery
            })
          });
          const searchData = await searchResponse.json();
          if (searchData.success && searchData.pages.length > 0) {
            // Append to first result
            // This would need append functionality
            result = { success: true, message: 'Found pages, append functionality coming soon' };
          } else {
            result = { success: false, error: 'No pages found' };
          }
          break;
        }
          
        case 'auto_tag':
          if (!tagContent) {
            alert('Please enter content to tag');
            setIsProcessing(false);
            return;
          }
          // This would call Knowledge Engine or AI service
          result = { success: true, tags: ['sample', 'tag'], message: 'Auto tagging coming soon' };
          break;
          
        case 'extract_entities':
          if (!entityContent) {
            alert('Please enter content to extract entities from');
            setIsProcessing(false);
            return;
          }
          // This would call Knowledge Engine or NLP service
          result = { success: true, entities: {}, message: 'Entity extraction coming soon' };
          break;
          
        default:
          result = { success: false, error: 'Unknown operation' };
      }
      
      setIsProcessing(false);
      setResults(prev => [...prev, {
        id: Date.now(),
        operation: operationId,
        status: result.success ? 'completed' : 'error',
        result: result,
        timestamp: new Date().toISOString()
      }]);
      
      if (result.success) {
        // Clear inputs after success
        setNotionContent('');
        setTagContent('');
        setEntityContent('');
        setAppendContent('');
        setSearchQuery('');
      }
    } catch (error: any) {
      setIsProcessing(false);
      setResults(prev => [...prev, {
        id: Date.now(),
        operation: operationId,
        status: 'error',
        error: error.message,
        timestamp: new Date().toISOString()
      }]);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--surface)]">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/elevation/workflow" className="inline-flex items-center gap-2 text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 mb-4">
            <ArrowLeft className="h-4 w-4" />
            Back to Workflow
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-[var(--green-dim)] flex items-center justify-center">
              <Tag className="h-6 w-6 text-[var(--green)]" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-[var(--text-pri)]">Tagging & Categorizing</h1>
              <p className="text-[var(--text-sec)]">Add metadata and structure to collected content</p>
            </div>
          </div>
        </div>

        {/* Engine Status */}
        <div className="bg-[var(--bg-raised)] shadow-sm border p-6 mb-8">
          <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-4">Knowledge Engine Status</h2>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-[var(--green)] rounded-full"></div>
              <span className="text-sm text-[var(--text-sec)]">Online</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-[var(--text-ter)]" />
              <span className="text-sm text-[var(--text-sec)]">Last activity: 1 minute ago</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-[var(--green)]" />
              <span className="text-sm text-[var(--text-sec)]">Ready for operations</span>
            </div>
          </div>
        </div>

        {/* Operations */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-[var(--text-pri)] mb-6">Available Operations</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {operations.map((operation) => {
              const Icon = operation.icon;
              const colors = getColorClasses(operation.color);
              
              return (
                <div
                  key={operation.id}
                  className={`${colors.bg} ${colors.border} border-2  p-6 ${colors.hover} transition-all duration-300`}
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`w-12 h-12 ${colors.badge}  flex items-center justify-center`}>
                      <Icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className={`text-lg font-semibold ${colors.text}`}>
                        {operation.name}
                      </h3>
                      <p className={`text-sm ${colors.text}`}>
                        {operation.description}
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-3 mb-4">
                    <div>
                      <span className="text-xs font-medium text-[var(--text-ter)]">INPUT:</span>
                      <p className="text-sm text-[var(--text-sec)]">{operation.input}</p>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-[var(--text-ter)]">OUTPUT:</span>
                      <p className="text-sm text-[var(--text-sec)]">{operation.output}</p>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-[var(--text-ter)]">EXAMPLE:</span>
                      <p className="text-sm text-[var(--text-sec)]">{operation.example}</p>
                    </div>
                  </div>

                  {/* Input Fields */}
                  <div className="space-y-3 mb-4 border-t pt-4">
                    {operation.id === 'create_notion_page' && (
                      <>
                        <label className="block text-sm font-medium text-[var(--text-sec)]">Content</label>
                        <textarea
                          value={notionContent}
                          onChange={(e) => setNotionContent(e.target.value)}
                          placeholder="Enter content for the Notion page..."
                          rows={4}
                          className="w-full border border-[var(--rule)] px-3 py-2 text-sm"
                          disabled={isProcessing}
                        />
                        <label className="block text-sm font-medium text-[var(--text-sec)]">Notion API Key</label>
                        <input
                          type="password"
                          value={notionApiKey}
                          onChange={(e) => setNotionApiKey(e.target.value)}
                          placeholder="notion_..."
                          className="w-full border border-[var(--rule)] px-3 py-2 text-sm"
                          disabled={isProcessing}
                        />
                        <label className="block text-sm font-medium text-[var(--text-sec)]">Database ID</label>
                        <input
                          type="text"
                          value={notionDatabaseId}
                          onChange={(e) => setNotionDatabaseId(e.target.value)}
                          placeholder="32-character database ID"
                          className="w-full border border-[var(--rule)] px-3 py-2 text-sm"
                          disabled={isProcessing}
                        />
                      </>
                    )}
                    
                    {operation.id === 'search_append' && (
                      <>
                        <label className="block text-sm font-medium text-[var(--text-sec)]">Search Query</label>
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Search for existing page..."
                          className="w-full border border-[var(--rule)] px-3 py-2 text-sm mb-2"
                          disabled={isProcessing}
                        />
                        <label className="block text-sm font-medium text-[var(--text-sec)]">Content to Append</label>
                        <textarea
                          value={appendContent}
                          onChange={(e) => setAppendContent(e.target.value)}
                          placeholder="Content to append to the page..."
                          rows={3}
                          className="w-full border border-[var(--rule)] px-3 py-2 text-sm mb-2"
                          disabled={isProcessing}
                        />
                        <label className="block text-sm font-medium text-[var(--text-sec)]">Notion API Key</label>
                        <input
                          type="password"
                          value={notionApiKey}
                          onChange={(e) => setNotionApiKey(e.target.value)}
                          placeholder="notion_..."
                          className="w-full border border-[var(--rule)] px-3 py-2 text-sm mb-2"
                          disabled={isProcessing}
                        />
                        <label className="block text-sm font-medium text-[var(--text-sec)]">Database ID</label>
                        <input
                          type="text"
                          value={notionDatabaseId}
                          onChange={(e) => setNotionDatabaseId(e.target.value)}
                          placeholder="32-character database ID"
                          className="w-full border border-[var(--rule)] px-3 py-2 text-sm"
                          disabled={isProcessing}
                        />
                      </>
                    )}
                    
                    {operation.id === 'auto_tag' && (
                      <>
                        <label className="block text-sm font-medium text-[var(--text-sec)]">Content to Tag</label>
                        <textarea
                          value={tagContent}
                          onChange={(e) => setTagContent(e.target.value)}
                          placeholder="Paste content here to generate tags..."
                          rows={6}
                          className="w-full border border-[var(--rule)] px-3 py-2 text-sm"
                          disabled={isProcessing}
                        />
                      </>
                    )}
                    
                    {operation.id === 'extract_entities' && (
                      <>
                        <label className="block text-sm font-medium text-[var(--text-sec)]">Content to Analyze</label>
                        <textarea
                          value={entityContent}
                          onChange={(e) => setEntityContent(e.target.value)}
                          placeholder="Paste content here to extract entities..."
                          rows={6}
                          className="w-full border border-[var(--rule)] px-3 py-2 text-sm"
                          disabled={isProcessing}
                        />
                      </>
                    )}
                  </div>
                  
                  <button
                    onClick={() => handleOperation(operation.id)}
                    disabled={isProcessing || 
                      (operation.id === 'create_notion_page' && (!notionContent || !notionDatabaseId || !notionApiKey)) ||
                      (operation.id === 'search_append' && (!searchQuery || !appendContent || !notionDatabaseId || !notionApiKey)) ||
                      (operation.id === 'auto_tag' && !tagContent) ||
                      (operation.id === 'extract_entities' && !entityContent)
                    }
                    className={`w-full py-2 px-4  font-medium transition-colors ${
                      isProcessing
                        ? 'bg-[var(--rule-bright)] text-[var(--text-ter)] cursor-not-allowed'
                        : `${colors.badge} ${colors.text} hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed`
                    }`}
                  >
                    {isProcessing && selectedOperation === operation.id ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                        Processing...
                      </div>
                    ) : (
                      'Execute Operation'
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Processing Results */}
        {results.length > 0 && (
          <div className="bg-[var(--bg-raised)] shadow-sm border p-6">
            <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-4">Processing Results</h2>
            <div className="space-y-3">
              {results.map((result) => {
                const operation = operations.find(op => op.id === result.operation);
                const isError = result.status === 'error' || result.result?.error;
                
                return (
                  <div key={result.id} className={`p-4  ${isError ? 'bg-[var(--coral-dim)] border border-[var(--coral)]' : 'bg-[var(--surface)]'}`}>
                    <div className="flex items-start gap-3">
                      {isError ? (
                        <AlertCircle className="h-5 w-5 text-[var(--coral)] flex-shrink-0 mt-0.5" />
                      ) : (
                        <CheckCircle className="h-5 w-5 text-[var(--green)] flex-shrink-0 mt-0.5" />
                      )}
                      <div className="flex-1">
                        <p className="text-sm font-medium text-[var(--text-pri)]">
                          {operation?.name} {isError ? 'failed' : 'completed'}
                        </p>
                        <p className="text-xs text-[var(--text-ter)] mt-1">
                          {new Date(result.timestamp).toLocaleString()}
                        </p>
                        {result.result && (
                          <div className="mt-2">
                            {result.result.error && (
                              <p className="text-sm text-[var(--coral)]">Error: {result.result.error}</p>
                            )}
                            {result.result.success && (
                              <div className="mt-2 p-2 bg-[var(--bg-raised)] rounded border text-xs">
                                <pre className="text-[var(--text-sec)] whitespace-pre-wrap">
                                  {JSON.stringify(result.result, null, 2).substring(0, 300)}
                                  {JSON.stringify(result.result, null, 2).length > 300 ? '...' : ''}
                                </pre>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${
                        isError ? 'bg-[var(--coral-dim)] text-[var(--coral)]' : 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]'
                      }`}>
                        {result.status}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Next Steps */}
        <div className="bg-[var(--bg-raised)] shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Next Steps</h3>
          <p className="text-[var(--text-sec)] mb-4">
            Once content is tagged and structured, move to the formulation phase to develop ideas.
          </p>
          <Link
            to="/elevation/workflow/formulating"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] transition-colors"
          >
            Go to Formulating Phase
            <ArrowLeft className="h-4 w-4 rotate-180" />
          </Link>
        </div>
      </div>
    </div>
  );
}
